import { hashWords, toUnitFloat } from "daedalus-core";

/**
 * The copies an emoji pops out as (ADR-114's amendment of 2026-10-09, points 1 and 5). A pop starts `EMOJI_COPIES`
 * copies at the character's middle; each waits up to `COPY_WAIT_FRAMES`, then lives `COPY_LIFE` frames: it flies outward
 * along an angle in the fan `COPY_FAN`, which reaches all round but for the bottom, `COPY_REACH` logical pixels with a
 * quadratic ease out, while drifting `COPY_DRIFT` further up at an even pace, at full strength for the first half of its
 * life and fading a step a frame over the second, its last frame one step above nothing. A pop's copies are spread one
 * to each twentieth of the fan, jittered inside it, so no pop clumps to one side.
 *
 * Every number of a path comes from the shared hash over the character's handle, the page's count of pops, the copy's
 * number and which number it is, through a buffer made once, so a path is the same for the same inputs and writing one
 * allocates nothing. Nothing about a path is sent; two viewers may see different paths.
 *
 * @example
 * ```ts
 * const path = copyPath(handle, pop, index, newCopyPath());
 * const motion = newCopyMotion();
 * if (copyMotion(framesSincePop, path.cos, path.sin, path.reach, path.drift, path.wait, path.life, motion)) {
 *   draw(centreX + motion.dx * scale, centreY + motion.dy * scale, motion.alpha);
 * }
 * ```
 */

/**
 * How many copies an emoji pops into.
 */
export const EMOJI_COPIES = 12;

/**
 * The most frames a copy waits after its pop before it is drawn.
 */
export const COPY_WAIT_FRAMES = 2;

/**
 * The frames a copy lives once it is drawn, both ends included.
 */
export const COPY_LIFE: Readonly<{ from: number; to: number }> = Object.freeze({ from: 32, to: 46 });

/**
 * The frames from a pop until its last copy is gone.
 */
export const COPY_SPAN_FRAMES = COPY_WAIT_FRAMES + COPY_LIFE.to;

/**
 * The fan a copy flies along, in degrees anticlockwise from straight right: from 20 below the horizontal on the right,
 * over the top, to 20 below it on the left.
 */
export const COPY_FAN: Readonly<{ from: number; to: number }> = Object.freeze({ from: -20, to: 200 });

/**
 * How far a copy flies outward, in logical pixels, the upper end excluded.
 */
export const COPY_REACH: Readonly<{ from: number; to: number }> = Object.freeze({ from: 16, to: 40 });

/**
 * How far a copy drifts further up over its life, in logical pixels, the upper end excluded.
 */
export const COPY_DRIFT: Readonly<{ from: number; to: number }> = Object.freeze({ from: 8, to: 20 });

/**
 * The seed every copy's hash starts from.
 */
const COPY_SEED = 0x656d6f6a;

/**
 * The largest handle a snapshot carries.
 */
const MAX_HANDLE = 0xffff;

/**
 * Which number of a path a hash is for, the hash's fourth word.
 */
const ANGLE = 0;
const REACH = 1;
const DRIFT = 2;
const WAIT = 3;
const LIFE = 4;

/**
 * The hash's input, made once: the handle, the pop, the copy's number and which number of its path.
 */
const words = new Uint32Array(4);

/**
 * A copy's path: the direction it flies (its cosine across and sine up), how far it flies and drifts in logical pixels,
 * how many frames it waits and how many it lives.
 */
export interface CopyPath {
  cos: number;
  sin: number;
  reach: number;
  drift: number;
  wait: number;
  life: number;
}

/**
 * A copy's place and strength at a frame, written in place: how far it stands from the pop's centre in logical pixels,
 * across and down (up is negative), and its strength from 0 to 1.
 */
export interface CopyMotion {
  dx: number;
  dy: number;
  alpha: number;
}

/**
 * A path to write into.
 *
 * @returns A path straight up that neither flies nor drifts, waits nor lives.
 */
export function newCopyPath(): CopyPath {
  return { cos: 0, sin: 1, reach: 0, drift: 0, wait: 0, life: 0 };
}

/**
 * A motion to write into.
 *
 * @returns A copy at the pop's centre at full strength.
 */
export function newCopyMotion(): CopyMotion {
  return { dx: 0, dy: 0, alpha: 1 };
}

/**
 * A number from 0 up to 1 for one number of a copy's path.
 *
 * @param which Which number of the path.
 * @returns The hash's unit float.
 */
function unit(which: number): number {
  words[3] = which;
  return toUnitFloat(hashWords(COPY_SEED, words, 4));
}

/**
 * Writes a copy's path.
 *
 * @param handle The character's handle, 0 to 65535.
 * @param pop The page's count of pops when this one popped, a whole number from 0 below 2^32.
 * @param index The copy's number in its pop, 0 below `EMOJI_COPIES`.
 * @param out Where to write the path.
 * @returns `out`.
 * @throws RangeError when the handle, the pop or the copy's number is out of its range.
 */
export function copyPath(handle: number, pop: number, index: number, out: CopyPath): CopyPath {
  if (!Number.isInteger(handle) || handle < 0 || handle > MAX_HANDLE) {
    throw new RangeError(`a copy's handle must be a whole number from 0 to ${MAX_HANDLE}, not ${handle}`);
  }
  if (!Number.isInteger(pop) || pop < 0 || pop > 0xffffffff) {
    throw new RangeError(`a pop's count must be a whole number from 0 below 2^32, not ${pop}`);
  }
  if (!Number.isInteger(index) || index < 0 || index >= EMOJI_COPIES) {
    throw new RangeError(`a copy's number must be a whole number from 0 below ${EMOJI_COPIES}, not ${index}`);
  }
  words[0] = handle;
  words[1] = pop;
  words[2] = index;
  const share = (COPY_FAN.to - COPY_FAN.from) / EMOJI_COPIES;
  const angle = ((COPY_FAN.from + share * (index + unit(ANGLE))) * Math.PI) / 180;
  out.cos = Math.cos(angle);
  out.sin = Math.sin(angle);
  out.reach = COPY_REACH.from + (COPY_REACH.to - COPY_REACH.from) * unit(REACH);
  out.drift = COPY_DRIFT.from + (COPY_DRIFT.to - COPY_DRIFT.from) * unit(DRIFT);
  out.wait = Math.floor(unit(WAIT) * (COPY_WAIT_FRAMES + 1));
  out.life = COPY_LIFE.from + Math.floor(unit(LIFE) * (COPY_LIFE.to - COPY_LIFE.from + 1));
  return out;
}

/**
 * A copy's place and strength a number of frames after its pop. Its path's numbers are passed one by one so a caller
 * holding paths in typed arrays reads them without making an object.
 *
 * @param frame Whole frames since the pop, from 0.
 * @param cos Its direction across.
 * @param sin Its direction up.
 * @param reach How far it flies outward, in logical pixels.
 * @param drift How far it drifts further up, in logical pixels.
 * @param wait The frames it waits before it is drawn.
 * @param life The frames it lives once drawn.
 * @param out Where to write the motion.
 * @returns Whether it is drawn on that frame: false while it waits and once its life is over, when `out` is left as it
 *   was.
 */
export function copyMotion(frame: number, cos: number, sin: number, reach: number, drift: number, wait: number, life: number, out: CopyMotion): boolean {
  const age = frame - wait;
  if (age < 0 || age >= life) {
    return false;
  }
  const done = age / life;
  const eased = 1 - (1 - done) * (1 - done);
  out.dx = cos * reach * eased + 0;
  out.dy = 0 - (sin * reach * eased + drift * done);
  const half = Math.floor(life / 2);
  out.alpha = age < half ? 1 : 1 - (age - half) / (life - half);
  return true;
}
